import { useEffect, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { ArtTabs, useIdpService } from "@art/shared-ui";
import { useSelector } from "react-redux";
import { usePageWrapper } from "../../../../hooks";
import { RootState } from "../../../../store";
import { useAttributePermission } from "../../../permissions";

export interface RoleSummary {
  id: number;
  name: string;
  description: string;
  is_master?: boolean;
  is_default?: boolean;
  is_tenant_scoped?: boolean;
  tenant_id?: number | null;
}

export interface RoleDetailContext {
  role: RoleSummary | null;
  reloadRole: () => void;
  canManageRole: boolean;
  canManageAccess: boolean;
  canManageResourceScopes: boolean;
}

const RoleDetail = () => {
  const { id } = useParams<{ id: string }>();
  const idpService = useIdpService();
  const location = useLocation();
  const tenantName = useSelector((state: RootState) => state.auth.tenantName);
  const isMasterTenant = useSelector((state: RootState) => Boolean(state.auth.user?.tenant?.is_master));
  const canManageAccess = useAttributePermission(
    "PLATFORM_SETTINGS",
    "ROLE_MANAGEMENT",
    "ManageRolePermissions",
  );
  const canManageResourceScopes = useAttributePermission(
    "PLATFORM_SETTINGS",
    "ROLE_MANAGEMENT",
    "ManageRoleScopes",
  );

  const [role, setRole] = useState<RoleSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const loadRole = async () => {
    if (!tenantName || !id) return;
    setLoading(true);
    try {
      const res = await idpService.get(`api/tenants/${tenantName}/roles/${id}`);
      setRole(res?.data?.data || null);
    } catch (err) {
      console.error(err);
      setRole(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRole();
  }, [tenantName, id]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1100 }}>
        <Skeleton variant="text" width={200} height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={320} height={36} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={480} height={20} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (!role) {
    return (
      <Box>
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }} spacing={1}>
          <Typography variant="h6" color="text.secondary">
            Role not found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            It may have been deleted or you may not have access to it.
          </Typography>
        </Stack>
      </Box>
    );
  }

  const canManageRole = !role.is_default && (isMasterTenant || role.tenant_id != null);

  return (
    <Box sx={{ maxWidth: 1400 }}>
      {/* Tabs */}
      <ArtTabs
        tabs={[
          { label: "Overview", route: `/platform-settings/roles-permissions/${id}/details` },
          ...(canManageAccess
            ? [{ label: "ABAC Access", route: `/platform-settings/roles-permissions/${id}/abac` }]
            : []),
          ...(canManageResourceScopes
            ? [{ label: "Resource Access", route: `/platform-settings/roles-permissions/${id}/resource-access` }]
            : []),
        ]}
        currentPath={location.pathname}
      />

      <Box sx={{ py: 3 }}>
        <Outlet context={{
          role,
          reloadRole: loadRole,
          canManageRole,
          canManageAccess,
          canManageResourceScopes,
        } satisfies RoleDetailContext} />
      </Box>
    </Box>
  );
};

export default usePageWrapper(RoleDetail, {
  page_title: "Role",
});
